import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { Loader2, Building2, Users, CalendarDays, IndianRupee, Briefcase } from "lucide-react";

const EMPTY = {
  company_name: "", contact_email: "", designation: "", skills: "",
  num_employees: "1", expected_date: "", offered_salary: "", location: "",
};

export function PostRequirementDialog({ mode = "public", trigger, onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      designation: f.designation,
      skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
      num_employees: parseInt(f.num_employees || "1", 10) || 1,
      expected_date: f.expected_date,
      offered_salary: f.offered_salary,
      location: f.location,
    };
    if (mode === "public") {
      payload.company_name = f.company_name;
      payload.contact_email = f.contact_email;
    } else {
      payload.company_name = f.company_name || "Company";
    }
    try {
      const url = mode === "employer" ? "/employer/requirements" : "/requirements";
      await api.post(url, payload);
      toast.success(t("requirement.success"));
      setF(EMPTY);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(t("requirement.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="requirement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-amber-600" /> {t("requirement.title")}
          </DialogTitle>
          <DialogDescription className="text-base">{t("requirement.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {mode === "public" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="rq-company">{t("requirement.company")}</Label>
                <div className="relative mt-1.5">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="rq-company" required value={f.company_name} onChange={set("company_name")} className="pl-9" data-testid="req-company" />
                </div>
              </div>
              <div>
                <Label htmlFor="rq-email">{t("requirement.contactEmail")}</Label>
                <Input id="rq-email" type="email" required value={f.contact_email} onChange={set("contact_email")} className="mt-1.5" data-testid="req-email" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="rq-designation">{t("requirement.designation")}</Label>
            <Input id="rq-designation" required placeholder={t("requirement.designationPlaceholder")} value={f.designation} onChange={set("designation")} className="mt-1.5" data-testid="req-designation" />
          </div>
          <div>
            <Label htmlFor="rq-skills">{t("requirement.skills")}</Label>
            <Input id="rq-skills" placeholder={t("requirement.skillsPlaceholder")} value={f.skills} onChange={set("skills")} className="mt-1.5" data-testid="req-skills" />
            <p className="mt-1 text-xs text-slate-400">{t("requirement.skillsHint")}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="rq-num">{t("requirement.numEmployees")}</Label>
              <div className="relative mt-1.5">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="rq-num" type="number" min="1" value={f.num_employees} onChange={set("num_employees")} className="pl-9" data-testid="req-num" />
              </div>
            </div>
            <div>
              <Label htmlFor="rq-date">{t("requirement.expectedDate")}</Label>
              <div className="relative mt-1.5">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="rq-date" type="date" value={f.expected_date} onChange={set("expected_date")} className="pl-9" data-testid="req-date" />
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="rq-salary">{t("requirement.offeredSalary")}</Label>
              <div className="relative mt-1.5">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="rq-salary" placeholder={t("requirement.salaryPlaceholder")} value={f.offered_salary} onChange={set("offered_salary")} className="pl-9" data-testid="req-salary" />
              </div>
            </div>
            <div>
              <Label htmlFor="rq-location">{t("requirement.location")}</Label>
              <Input id="rq-location" placeholder={t("requirement.locationPlaceholder")} value={f.location} onChange={set("location")} className="mt-1.5" data-testid="req-location" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-base font-bold" data-testid="req-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("requirement.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PostRequirementDialog;
